import { useDisabledGraphViews } from '@/graph/instanceGraphViews'
import { CirclesGraphViews } from '@/graph/types'
import {
  Button,
  ButtonProps,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
} from '@chakra-ui/react'
import { GraphView } from '@rolebase/shared/model/graph'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from 'src/icons'

interface Props extends Omit<ButtonProps, 'value' | 'onChange'> {
  value: GraphView
  onChange: (value: GraphView) => void
  // Offer the members option, on by default. The share panel leaves it out:
  // publishing the members is a setting of the organization, not of a link.
  membersOption?: boolean
  // Offer the whiteboard (#200). The org chart and the org's default view do
  // (#210); it is not a graph, so it cannot be an export or a share link.
  whiteboardOption?: boolean
}

// Order decided by Yohan (#94, Q5, 24/09/2026): the two circle views of #107
// right after Circles, the role links after the tree; the whiteboard right
// after Network (#200, 13/10/2026). The views switched off for the
// organization are left out in the component (#208, #210).
const viewsList = [
  CirclesGraphViews.Circles,
  CirclesGraphViews.LinkedCircles,
  CirclesGraphViews.Network,
  CirclesGraphViews.Whiteboard,
  CirclesGraphViews.Tree,
  CirclesGraphViews.RoleLinks,
  CirclesGraphViews.Members,
]

// Which options change the drawing of each view. An option without effect on
// the current view stays in place, disabled, and says why: the menu keeps the
// same shape from one view to the next (#94, Q5).
const foldableViews = [CirclesGraphViews.Circles, CirclesGraphViews.Tree]
// The network's crown roles carry their members too (measured on a real org:
// 444 nodes with them, 246 without)
const membersViews = [
  CirclesGraphViews.Circles,
  CirclesGraphViews.Tree,
  CirclesGraphViews.LinkedCircles,
  CirclesGraphViews.Network,
]

export default function GraphViewsSelect({
  value,
  onChange,
  membersOption = true,
  whiteboardOption = false,
  ...buttonProps
}: Props) {
  const { t } = useTranslation()
  const disabledViews = useDisabledGraphViews()

  const { view, folded, members } = value
  // The members view shows the whole organization by definition, and is made
  // of members: neither option applies to it
  const optionable = view !== CirclesGraphViews.Members

  const label = t(`GraphViewsSelect.${view}` as any)
  const canFold = foldableViews.includes(view)
  const canShowMembers = membersViews.includes(view)

  const options: string[] = []
  if (canFold && folded) options.push('folded')
  if (canShowMembers && membersOption && members) options.push('members')

  return (
    <Menu>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon size="1em" />}
        {...buttonProps}
      >
        {canFold && folded
          ? t('GraphViewsSelect.foldedLabel', { view: label })
          : label}
      </MenuButton>

      <MenuList zIndex={2000} shadow="md" maxW="330px">
        <MenuOptionGroup type="radio" value={view}>
          {viewsList
            .filter(
              (itemView) =>
                !disabledViews.includes(itemView) &&
                (whiteboardOption || itemView !== CirclesGraphViews.Whiteboard)
            )
            .map((itemView) => (
              <MenuItemOption
                key={itemView}
                value={itemView}
                alignItems="start"
                pt={2}
                onClick={() => onChange({ view: itemView, folded, members })}
              >
                <Flex flexDirection="column" alignItems="left" mt={-2} mb={2}>
                  <Text fontWeight="bold">
                    {t(`GraphViewsSelect.${itemView}` as any)}
                  </Text>
                  <Text fontSize="sm">
                    {t(`GraphViewsSelect.${itemView}_desc` as any)}
                  </Text>
                </Flex>
              </MenuItemOption>
            ))}
        </MenuOptionGroup>

        {optionable && (
          <>
            {/* The view descriptions are tall: a hairline gets lost between
                them */}
            <MenuDivider
              my={3}
              opacity={1}
              borderBottomWidth="2px"
              borderColor="gray.200"
              _dark={{ borderColor: 'gray.550' }}
            />
            <MenuOptionGroup type="checkbox" value={options}>
              <MenuItemOption
                value="folded"
                alignItems="start"
                pt={2}
                closeOnSelect={false}
                isDisabled={!canFold}
                onClick={() => onChange({ view, folded: !folded, members })}
              >
                <Flex flexDirection="column" alignItems="left" mt={-2} mb={2}>
                  <Text fontWeight="bold">{t('GraphViewsSelect.folded')}</Text>
                  <Text fontSize="sm">
                    {canFold
                      ? t('GraphViewsSelect.folded_desc')
                      : t('GraphViewsSelect.optionNotApplicable', {
                          view: label,
                        })}
                  </Text>
                </Flex>
              </MenuItemOption>

              {membersOption && (
                <MenuItemOption
                  value="members"
                  alignItems="start"
                  pt={2}
                  closeOnSelect={false}
                  isDisabled={!canShowMembers}
                  onClick={() => onChange({ view, folded, members: !members })}
                >
                  <Flex flexDirection="column" alignItems="left" mt={-2} mb={2}>
                    <Text fontWeight="bold">
                      {t('GraphViewsSelect.members')}
                    </Text>
                    <Text fontSize="sm">
                      {canShowMembers
                        ? t('GraphViewsSelect.members_desc')
                        : t('GraphViewsSelect.optionNotApplicable', {
                            view: label,
                          })}
                    </Text>
                  </Flex>
                </MenuItemOption>
              )}
            </MenuOptionGroup>
          </>
        )}
      </MenuList>
    </Menu>
  )
}
